import React, { FC, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import { routes } from "@app/routing";
import { GisIntakeStep, STEP_ROUTE_SUFFIX } from "@app/service/intakeForm";
import { COUNTRIES, LANGUAGES } from "@app/utils/intakeFormOptions";
import { IntakeFormStepLayout, InlineCheckbox } from "../components";
import { useIntakeForm } from "../context";

const messages = defineMessages({
  title: {
    id: "intakeStepLocationInterpreter.title",
    defaultMessage: "Confirm your location and interpreter preference",
  },
  countryLabel: {
    id: "intakeStepLocationInterpreter.countryLabel",
    defaultMessage: "Country/Region of Residence",
  },
  countryPlaceholder: {
    id: "intakeStepLocationInterpreter.countryPlaceholder",
    defaultMessage: "Select country / region",
  },
  interpreterCheckbox: {
    id: "intakeStepLocationInterpreter.interpreterCheckbox",
    defaultMessage: "I need an interpreter for my appointment",
  },
  languageLabel: {
    id: "intakeStepLocationInterpreter.languageLabel",
    defaultMessage: "What language?",
  },
  languagePlaceholder: {
    id: "intakeStepLocationInterpreter.languagePlaceholder",
    defaultMessage: "Select language",
  },
  nextButton: {
    id: "intakeStepLocationInterpreter.nextButton",
    defaultMessage: "Next",
  },
});

const IntakeStepLocationInterpreter: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { orderUidPublic, testUid } = useParams<{
    orderUidPublic: string;
    testUid: string;
  }>();
  const {
    userInputFields,
    updateUserField,
    setCurrentStep,
    totalSteps,
    getNextStep,
    getPreviousStep,
  } = useIntakeForm();

  const currentStep = GisIntakeStep.LOCATION_INTERPRETER;

  const [selectedCountry, setSelectedCountry] = useState<string>(
    userInputFields.countryOfResidence || "US"
  );
  const [needsInterpreter, setNeedsInterpreter] = useState<boolean>(
    userInputFields.interpreterNeeded ?? false
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    userInputFields.interpreterLanguage || ""
  );

  useEffect(() => {
    setCurrentStep(currentStep);
    if (!userInputFields.countryOfResidence) {
      updateUserField("countryOfResidence", "US");
    }
  }, [setCurrentStep]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCountry(value);
    updateUserField("countryOfResidence", value || null);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedLanguage(value);
    updateUserField("interpreterLanguage", value || null);
  };

  const isContinueDisabled = () => {
    if (!selectedCountry) return true;
    if (needsInterpreter && !selectedLanguage) return true;
    return false;
  };

  const buildRoute = (step: GisIntakeStep | null) =>
    routes.scheduleAGeneticInformationSessionIntakePage(
      orderUidPublic || "",
      testUid || ""
    ) + (step ? STEP_ROUTE_SUFFIX[step] : "");

  const handleContinue = () => {
    if (isContinueDisabled()) return;

    updateUserField("countryOfResidence", selectedCountry);
    if (needsInterpreter) {
      updateUserField("interpreterLanguage", selectedLanguage || null);
    }
    updateUserField("interpreterNeeded", needsInterpreter);

    navigate(buildRoute(getNextStep(currentStep)));
  };

  const handleBack = () => {
    navigate(buildRoute(getPreviousStep(currentStep)));
  };

  return (
    <IntakeFormStepLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      title={intl.formatMessage(messages.title)}
      onContinue={handleContinue}
      onBack={handleBack}
      continueDisabled={isContinueDisabled()}
      continueLabel={intl.formatMessage(messages.nextButton)}
    >
      <div className="intake-form-step__field-group">
        <label
          className="intake-form-step__field-label"
          htmlFor="country-select"
        >
          {intl.formatMessage(messages.countryLabel)}
          <span className="intake-form-step__field-label__required"> *</span>
        </label>
        <select
          id="country-select"
          className={`intake-form-step__select${!selectedCountry ? " intake-form-step__select--placeholder" : ""}`}
          value={selectedCountry}
          onChange={handleCountryChange}
        >
          <option value="">
            {intl.formatMessage(messages.countryPlaceholder)}
          </option>
          {COUNTRIES.map((country) => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </select>
      </div>

      <InlineCheckbox
        id="interpreter-checkbox"
        label={intl.formatMessage(messages.interpreterCheckbox)}
        checked={needsInterpreter}
        onChange={(checked) => {
          setNeedsInterpreter(checked);
          updateUserField("interpreterNeeded", checked);
          if (!checked) {
            setSelectedLanguage("");
            updateUserField("interpreterLanguage", null);
          }
        }}
      />

      {needsInterpreter && (
        <div className="intake-form-step__field-group">
          <label
            className="intake-form-step__field-label"
            htmlFor="language-select"
          >
            {intl.formatMessage(messages.languageLabel)}
            <span className="intake-form-step__field-label__required"> *</span>
          </label>
          <select
            id="language-select"
            className={`intake-form-step__select${!selectedLanguage ? " intake-form-step__select--placeholder" : ""}`}
            value={selectedLanguage}
            onChange={handleLanguageChange}
          >
            <option value="">
              {intl.formatMessage(messages.languagePlaceholder)}
            </option>
            {LANGUAGES.map((language) => (
              <option key={language.value} value={language.value}>
                {language.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </IntakeFormStepLayout>
  );
};

export default IntakeStepLocationInterpreter;
